import { formatters } from '@/core';
import { useTranslation } from 'react-i18next';
import styles from './ProjectDashboard.module.scss';
import { Tooltip, Typography } from 'antd';
import classNames from 'classnames';

interface Props {
  percentage: number;
  itemsToAdd: number;
  tasksToComplete: number;
  isFinalized: boolean;
  name?: string;
  deadline?: string;
  size?: 'large';
  align?: 'center';
  noColor?: boolean;
}

const colorMap = {
  green: '#EAF7E5',
  blue: '#E8F4FE',
  orange: '#FAE2B5',
  yellow: '#FEFFBC',
  red: '#FADEDA',
  gray: '#F3F3F3',
  noColor: '',
};

function getProgressColor(props: Props) {
  const { percentage, isFinalized, deadline, noColor } = props;
  if (isFinalized || percentage === 100) return 'green';

  const now = new Date();
  const deadlineDate = deadline ? new Date(deadline) : null;

  if (noColor) return 'noColor';
  if (percentage === 0 || !deadlineDate) return 'gray';

  const diffDays = Math.floor(
    (deadlineDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffDays <= 1 && percentage < 100) return 'red';

  if (diffDays < 7) {
    if (percentage <= 50) return 'orange';
    if (percentage < 100) return 'yellow';
  }

  return 'blue';
}

interface IndicatorProps {
  percentage: number;
  isFinalized: boolean;
  itemsToAdd: number;
  tasksToComplete: number;
  size?: 'large';
}

const PercentageIndicator = (props: IndicatorProps) => {
  const { percentage, isFinalized, size, itemsToAdd, tasksToComplete } = props;
  const { t } = useTranslation();

  const tooltipText = (() => {
    if (isFinalized) {
      return t('projects.dashboard.stepFinalized');
    }

    const parts: string[] = [];

    if (tasksToComplete > 0) {
      parts.push(
        t(
          'projects.dashboard.tasksToComplete' +
            (tasksToComplete > 1 ? '_plural' : ''),
          {
            count: tasksToComplete,
          },
        ),
      );
    }

    if (itemsToAdd > 0) {
      parts.push(
        t('projects.dashboard.itemsToAdd' + (itemsToAdd > 1 ? '_plural' : ''), {
          count: itemsToAdd,
        }),
      );
    }

    if (parts.length === 0) {
      return t('projects.dashboard.allDone');
    }

    return t('projects.dashboard.needToComplete', {
      parts: parts.join(' ' + t('projects.dashboard.and') + ' '),
    });
  })();

  return (
    <Tooltip title={tooltipText}>
      <Typography.Title
        level={size === 'large' ? 3 : 5}
        className={size === 'large' ? 'mt-2' : 'mb-0'}
      >
        {percentage}% {isFinalized && t('projects.dashboard.finalized')}
      </Typography.Title>
    </Tooltip>
  );
};

export const ProjectHomeProgressCell = (props: Props) => {
  const {
    percentage,
    isFinalized,
    deadline,
    name,
    size,
    align,
    noColor,
    itemsToAdd,
    tasksToComplete,
  } = props;
  const color = getProgressColor(props);
  return (
    <div
      style={{
        backgroundColor: colorMap[color],
      }}
      className={classNames(
        styles.cell,
        align && styles.center,
        noColor && styles.border,
      )}
    >
      {name && <> {name}</>}

      <PercentageIndicator
        percentage={percentage}
        isFinalized={isFinalized}
        size={size}
        itemsToAdd={itemsToAdd}
        tasksToComplete={tasksToComplete}
      />
      <>{deadline ? formatters.dateTime(deadline, 'date') : '-'}</>
    </div>
  );
};
